import React from 'react';
import { Mail, Phone, MapPin, Shield, Globe, Award } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#040F2D] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-6">
              <img 
                src="/goldcif-logo.png" 
                alt="GOLDCIF Logo - Licensed Gold Buyers" 
                className="h-8 w-auto mr-3 opacity-90"
                loading="lazy"
                width="32"
                height="32"
              />
              <div className="text-3xl font-bold text-[#D4AF37]">GOLDCIF</div>
            </div>
            <p className="text-[#C5A059] mb-6 text-lg leading-relaxed">
              The trusted platform helping artisanal and small-scale gold sellers export their gold legally, 
              transparently, and securely from Africa to international markets.
            </p>
            <div className="flex space-x-6">
              <div className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-[#D4AF37]" />
                <span className="text-sm text-[#C5A059]">100% Legal Compliance</span>
              </div>
              <div className="flex items-center space-x-2">
                <Globe className="h-5 w-5 text-[#D4AF37]" />
                <span className="text-sm text-[#C5A059]">International Standards</span>
              </div>
              <div className="flex items-center space-x-2">
                <Award className="h-5 w-5 text-[#D4AF37]" />
                <span className="text-sm text-[#C5A059]">Verified Process</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-semibold mb-6 text-[#D4AF37]">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
                  className="text-[#C5A059] hover:text-[#D4AF37] transition-colors"
                  aria-label="Navigate to About GOLDCIF section"
                >
                  About GOLDCIF
                </button>
              </li>
              <li>
                <button
                  onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                  className="text-[#C5A059] hover:text-[#D4AF37] transition-colors"
                  aria-label="Learn how gold export works"
                >
                  How It Works
                </button>
              </li>
              <li>
                <button
                  onClick={() => document.getElementById('faq').scrollIntoView({ behavior: 'smooth' })}
                  className="text-[#C5A059] hover:text-[#D4AF37] transition-colors"
                  aria-label="View frequently asked questions"
                >
                  FAQ
                </button>
              </li>
              <li>
                <button
                  onClick={() => document.getElementById('blog').scrollIntoView({ behavior: 'smooth' })}
                  className="text-[#C5A059] hover:text-[#D4AF37] transition-colors"
                  aria-label="View gold export blog articles"
                >
                  Blog
                </button>
              </li>
              <li>
                <button
                  onClick={() => document.getElementById('requirements').scrollIntoView({ behavior: 'smooth' })}
                  className="text-[#C5A059] hover:text-[#D4AF37] transition-colors"
                  aria-label="View requirements to work with us"
                >
                  Requirements
                </button>
              </li>
              <li>
                <button
                  onClick={() => document.getElementById('application').scrollIntoView({ behavior: 'smooth' })}
                  className="text-[#C5A059] hover:text-[#D4AF37] transition-colors"
                  aria-label="Start gold export application"
                >
                  Apply Now
                </button>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-semibold mb-6 text-[#D4AF37]">Contact Information</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-[#C5A059]">Email</p>
                  <a 
                    href="mailto:compliance@goldcif.com" 
                    className="text-white hover:text-[#D4AF37] transition-colors"
                    aria-label="Send email to GOLDCIF"
                  >
                    compliance@goldcif.com
                  </a>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Phone className="h-5 w-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-[#C5A059]">WhatsApp Business</p>
                  <a 
                    href="https://wa.me/256764017856" 
                    className="text-white hover:text-[#D4AF37] transition-colors"
                    aria-label="Contact GOLDCIF Uganda Office via WhatsApp"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    +256 764 017 856
                  </a>
                  <p className="text-[#C5A059] text-sm mt-1">Uganda Office</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-[#C5A059]">Operations</p>
                  <p className="text-white">Uganda, Dubai</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-[#0B132B] mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-[#C5A059] text-sm">
              © {currentYear} GOLDCIF. All rights reserved. Licensed gold export operations.
            </div>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-[#C5A059] hover:text-[#D4AF37] text-sm transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-[#C5A059] hover:text-[#D4AF37] text-sm transition-colors">
                Terms of Service
              </a>
              <a href="#" className="text-[#C5A059] hover:text-[#D4AF37] text-sm transition-colors">
                Compliance
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;